import type { Payout } from '../types/payout';

// Data structures for FIFO inventory tracking
export interface Acquisition {
  transactionId: string;
  date: string;
  crypto: {
    id: string;
    symbol: string;
    name: string;
  };
  amount: number;
  costBasisPerUnit: number; // EUR (includes fees if applicable)
  remainingAmount: number;
  source: 'payout' | 'achat' | 'stacking' | 'swap_receive';
}

export interface CryptoInventory {
  cryptoId: string;
  acquisitions: Acquisition[]; // Historique des acquisitions (ordre chronologique)
  totalAmount: number;
  averageCostBasis: number;
}

interface Disposal {
  transactionId: string;
  date: string;
  crypto: {
    id: string;
    symbol: string;
    name: string;
  };
  amount: number;
  salePrice: number;
  totalSaleValue: number;
  source: 'vente' | 'swap_send';
}

/**
 * Construit une carte d'inventaire à partir des transactions (ordre chronologique).
 * Utilisé pour le tracking des quantités détenues par crypto.
 */
export const buildInventoryMap = (transactions: Payout[]): Map<string, CryptoInventory> => {
  const inventoryMap = new Map<string, CryptoInventory>();

  // Sort by date ascending for proper FIFO processing
  const sortedTransactions = [...transactions].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  sortedTransactions.forEach((tx) => {
    switch (tx.type) {
      case 'payout':
      case 'achat':
      case 'stacking':
        if (tx.crypto && tx.amount !== undefined && tx.unitPrice !== undefined) {
          addAcquisition(inventoryMap, tx, tx.type);
        }
        break;

      case 'swap':
        // Process sent crypto as disposal
        if (tx.sentCrypto && tx.sentAmount && tx.sentUnitPrice !== undefined) {
          processDisposal(inventoryMap, {
            transactionId: tx.id,
            date: tx.date,
            crypto: tx.sentCrypto,
            amount: tx.sentAmount,
            salePrice: tx.sentUnitPrice,
            totalSaleValue: tx.sentTotalValue || 0,
            source: 'swap_send',
          });
        }

        // Add received crypto as acquisition
        if (
          tx.receivedCrypto &&
          tx.receivedAmount &&
          tx.receivedUnitPrice !== undefined
        ) {
          addAcquisition(inventoryMap, tx, 'swap_receive', {
            crypto: tx.receivedCrypto,
            amount: tx.receivedAmount,
            unitPrice: tx.receivedUnitPrice,
            totalValue: tx.receivedTotalValue || 0,
            fees: tx.fees,
          });
        }
        break;

      case 'vente':
        if (tx.crypto && tx.amount !== undefined && tx.unitPrice !== undefined) {
          processDisposal(inventoryMap, {
            transactionId: tx.id,
            date: tx.date,
            crypto: tx.crypto,
            amount: tx.amount,
            salePrice: tx.unitPrice,
            totalSaleValue: tx.totalValue || 0,
            source: 'vente',
          });
        }
        break;
    }
  });

  return inventoryMap;
};

/**
 * Add an acquisition to the inventory (FIFO queue).
 * Fees are proportionally added to the cost basis per unit.
 */
export const addAcquisition = (
  inventoryMap: Map<string, CryptoInventory>,
  tx: Payout,
  source: 'payout' | 'achat' | 'stacking' | 'swap_receive',
  overrideFields?: {
    crypto: Payout['crypto'];
    amount: number;
    unitPrice: number;
    totalValue: number;
    fees?: number;
  }
): void => {
  const crypto = overrideFields?.crypto || tx.crypto;
  const amount = overrideFields?.amount ?? tx.amount;
  const unitPrice = overrideFields?.unitPrice ?? tx.unitPrice;
  const fees = overrideFields?.fees ?? tx.fees;

  if (!crypto || amount === undefined || unitPrice === undefined) return;

  const cryptoId = crypto.id;
  let inventory = inventoryMap.get(cryptoId);

  if (!inventory) {
    inventory = {
      cryptoId,
      acquisitions: [],
      totalAmount: 0,
      averageCostBasis: 0,
    };
    inventoryMap.set(cryptoId, inventory);
  }

  // Cost basis includes fees proportionally
  const feePerUnit = (fees || 0) / amount;
  const costBasisPerUnit = unitPrice + feePerUnit;

  const acquisition: Acquisition = {
    transactionId: tx.id,
    date: tx.date,
    crypto,
    amount,
    costBasisPerUnit,
    remainingAmount: amount,
    source,
  };

  inventory.acquisitions.push(acquisition);
  inventory.totalAmount += amount;

  // Recalculate average cost basis
  const totalCost = inventory.acquisitions.reduce(
    (sum, acq) => sum + acq.remainingAmount * acq.costBasisPerUnit,
    0
  );
  inventory.averageCostBasis =
    inventory.totalAmount > 0 ? totalCost / inventory.totalAmount : 0;
};

/**
 * Process a disposal (sale or swap) by consuming acquisitions in FIFO order.
 */
export const processDisposal = (
  inventoryMap: Map<string, CryptoInventory>,
  disposal: Disposal
): void => {
  const inventory = inventoryMap.get(disposal.crypto.id);

  if (!inventory) {
    console.warn(
      `No inventory for ${disposal.crypto.symbol} - disposal (${disposal.source}) before acquisition`
    );
    return;
  }

  if (inventory.totalAmount < disposal.amount) {
    console.warn(
      `Insufficient inventory for ${disposal.crypto.symbol}: have ${inventory.totalAmount}, trying to sell ${disposal.amount}`
    );
  }

  let remainingToSell = disposal.amount;

  // Consomme les acquisitions dans l'ordre chronologique
  for (const acquisition of inventory.acquisitions) {
    if (remainingToSell <= 0) break;

    const amountFromThisAcquisition = Math.min(acquisition.remainingAmount, remainingToSell);

    acquisition.remainingAmount -= amountFromThisAcquisition;
    remainingToSell -= amountFromThisAcquisition;
  }

  // Update total amount
  inventory.totalAmount = Math.max(0, inventory.totalAmount - disposal.amount);

  // Remove fully consumed acquisitions
  inventory.acquisitions = inventory.acquisitions.filter((acq) => acq.remainingAmount > 0);

  // Recalculate average cost basis
  const totalCost = inventory.acquisitions.reduce(
    (sum, acq) => sum + acq.remainingAmount * acq.costBasisPerUnit,
    0
  );
  inventory.averageCostBasis =
    inventory.totalAmount > 0 ? totalCost / inventory.totalAmount : 0;
};
